import { Component, OnInit, ViewChild } from '@angular/core';

import {} from '@angular/common/http';
import { Router } from '@angular/router';
import { FontAwesomeModule } from '@fortawesome/angular-fontawesome';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { TenantService } from '../../services/tenant.service';

import { ModelImportExportService } from '../../services/model-import-export.service';
import {
  faPlus,
  faTrash,
  faQuestionCircle,
  faSearch,
  faTimes,
  faFlask,
  faCheck,
  faEye,
  faPen,
  faTag,
  faUnlink,
  faDownload,
  faRocket,
  faHistory
} from '@fortawesome/free-solid-svg-icons';

import {
  HeaderActionsComponent,
  HeaderActionsConfig
} from '../../components/header-actions/header-actions.component';

import {
  SearchFilters
} from '../../components/search/search.component';

import {
  PaginationComponent
} from '../../components/pagination/pagination.component';

import {
  ItemListComponent,
  ListItem,
  ListConfig
} from '../../components/item-list/item-list.component';

import {
  ModelsService,
  Model
} from '../../services/models.service';

import { AlertService } from '../../components/alert-service/alert-service';
import { DateFormatterService } from '../../services/date-formatter.service';
import { TagManagerComponent, TaggableItem } from '../../components/tag-manager/tag-manager.component';
import { Tag } from '../../services/tags.service';
import { ModalDeleteConfirmComponent } from '../../components/modal-delete-confirm/modal-delete-confirm.component';
import { UsersService } from '../../services/users.service';
import { ModalDeployModelComponent } from '../../components/modal-deploy-model/modal-deploy-model.component';
import { ModalDeploymentHistoryComponent } from '../../components/modal-deployment-history/modal-deployment-history.component';

@Component({
  selector: 'app-models',
  standalone: true,
  imports: [
    FontAwesomeModule,
    TranslateModule,
    HeaderActionsComponent,
    ItemListComponent,
    TagManagerComponent,
    ModalDeleteConfirmComponent,
    PaginationComponent,
    ModalDeployModelComponent,
    ModalDeploymentHistoryComponent
],
  templateUrl: './models.component.html',
  styleUrls: ['./models.component.scss']
})
export class ModelsComponent implements OnInit {
  @ViewChild(TagManagerComponent) tagManager!: TagManagerComponent;

  models: Model[] = [];
  filteredModels: Model[] = [];
  loading = false;
  showDeleteModal = false;
  modelToDelete: ListItem | null = null;
  modelImpactItems: string[] = [];
  forceDeleteCheckboxLabel = '';
  forceDeleteCheckboxWarning = '';

  currentUser: any = null;
  showDeployModal = false;
  showHistoryModal = false;
  selectedModelForAction: Model | null = null;

  currentPage = 1;
  pageSize = 10;
  totalItems = 0;
  totalPages = 0;

  headerConfig: HeaderActionsConfig = {
    addButton: {
      type: 'add',
      text: 'BUTTON.NEW_MODEL',
      icon: faPlus,
      visible: true,
      disabled: false
    },
    helpButton: {
      type: 'help',
      icon: faQuestionCircle,
      visible: false,
      disabled: true
    },
    searchConfig: {
      showTextSearch: true,
      showTagFilter: true,
      textPlaceholder: 'SEARCH.PLACEHOLDER.MODELS',
      tagPlaceholder: 'SEARCH.PLACEHOLDER.TAGS'
    }
  };

  listConfig: ListConfig = {
    itemIcon: faFlask,
    itemIconClass: 'artifact-icon',
    showMetadata: true,
    showType: true,
    typeLabels: {
      extraction: 'SIMULATIONS.TYPE.EXTRACTION',
      classification: 'SIMULATIONS.TYPE.CLASSIFICATION',
      processing: 'SIMULATIONS.TYPE.PROCESSING',
    },
    emptyMessage: 'MESSAGE.NO.MODELS',
    emptyMessageFiltered: 'MESSAGE.NO.MODELS.FILTERED',
    actions: []
  };

  currentFilters: SearchFilters = {
    searchTerm: '',
    selectedTag: ''
  };

  deployFilter: 'all' | 'deployed' | 'not_deployed' = 'all';

  faPlus = faPlus;
  faTrash = faTrash;
  faQuestionCircle = faQuestionCircle;
  faSearch = faSearch;
  faTimes = faTimes;
  faFlask = faFlask;
  faCheck = faCheck;
  faEye = faEye;
  faPen = faPen;
  faTag = faTag;
  faUnlink = faUnlink;
  faDownload = faDownload;

  constructor(
    private service: ModelsService,
    private translate: TranslateService,
    private alert: AlertService,
    private router: Router,
    private importExportService: ModelImportExportService,
    private dateFormatter: DateFormatterService,
    private usersService: UsersService,
    private tenantService: TenantService
  ) {}

  ngOnInit(): void {
    this.usersService.getMe().subscribe({
      next: (res) => {
        this.currentUser = res.user;
        this.initializeActions();
        this.loadModels();
      },
      error: () => {
        this.initializeActions();
        this.loadModels();
      }
    });
  }

  initializeActions(): void {
    const actions = [
      {
        id: 'export',
        label: 'BUTTON.EXPORT',
        icon: faDownload,
        cssClass: 'export-item'
      },
      {
        id: 'link-tag',
        label: 'BUTTON.LINK.TAG',
        icon: faTag,
        cssClass: 'link-tag-item'
      },
      {
        id: 'unlink-tag',
        label: 'BUTTON.UNLINK.TAG',
        icon: faUnlink,
        cssClass: 'unlink-tag-item'
      }
    ];

    // if (this.currentUser?.provider === 'extraidados') {
      actions.push({
        id: 'deploy',
        label: 'BUTTON.DEPLOY',
        icon: faRocket,
        cssClass: 'deploy-item'
      });
      actions.push({
        id: 'history',
        label: 'BUTTON.HISTORY',
        icon: faHistory,
        cssClass: 'history-item'
      });
    // }

    actions.push({
      id: 'delete',
      label: 'BUTTON.DELETE',
      icon: faTrash,
      cssClass: 'delete-item'
    });

    this.listConfig.actions = actions;
  }

  onAddModel(): void {
    this.router.navigate(this.tenantService.buildRoute('/models/new'));
  }

  onFiltersChanged(filters: SearchFilters) {
    this.currentFilters = filters;
    this.applyFilters();
  }

  onSearchCleared() {
    this.currentFilters.searchTerm = '';
    this.applyFilters();
  }

  onTagFilterCleared() {
    this.currentFilters.selectedTag = '';
    this.applyFilters();
  }

  onAllFiltersCleared() {
    this.currentFilters = { searchTerm: '', selectedTag: '' };
    this.applyFilters();
  }

  onDeployFilterChange(value: 'all' | 'deployed' | 'not_deployed') {
    if (this.deployFilter === value) return;
    this.deployFilter = value;
    this.applyFilters();
  }

  get modelsAsListItems(): ListItem[] {
    return this.filteredModels.map(m => ({
      id: m.id ?? m.name,
      name: m.name,
      type: m['model_type'] || 'extraction',
      created_date: m.created_at ? this.dateFormatter.formatDate(m.created_at) : '',
      created_time: m.created_at ? this.dateFormatter.formatTime(m.created_at) : '',
      tag: m.tag || null,
      customIcon: m['is_deployed'] ? faCheck : undefined,
      customIconTitle: m['is_deployed'] ? 'MODELS.DEPLOYED' : undefined
    }));
  }

  get hasActiveFilters(): boolean {
    return !!this.currentFilters.searchTerm || !!this.currentFilters.selectedTag || this.deployFilter !== 'all';
  }

  private applyFilters(): void {
    this.currentPage = 1;
    this.loadModels();
  }

  onItemClick(item: ListItem): void {
    const id = String(item.id);
    if (!id) return;

    this.router.navigate(this.tenantService.buildRoute(`/models/${id}`));
  }

  onActionClick(event: { action: string; item: ListItem; event: Event }): void {
    if (event.action === 'edit') {
      this.editModel(event.item);
    }
    if (event.action === 'delete') {
      this.deleteModel(String(event.item.id), event.event);
    }
    if (event.action === 'export') {
      this.exportModel(String(event.item.id), event.event);
    }
    if (event.action === 'link-tag') {
      this.handleLinkTag(event.item);
    }
    if (event.action === 'unlink-tag') {
      this.handleUnlinkTag(event.item);
    }
    if (event.action === 'deploy') {
      this.openDeployModal(event.item);
    }
    if (event.action === 'history') {
      this.openHistoryModal(event.item);
    }
  }

  openDeployModal(item: ListItem): void {
    const model = this.models.find(m => (m.id ?? m.name) === item.id);
    if (model) {
      this.selectedModelForAction = model;
      this.showDeployModal = true;
    }
  }

  openHistoryModal(item: ListItem): void {
    const model = this.models.find(m => (m.id ?? m.name) === item.id);
    if (model) {
      this.selectedModelForAction = model;
      this.showHistoryModal = true;
    }
  }

  editModel(item: ListItem): void {
    const id = String(item.id);
    this.router.navigate(this.tenantService.buildRoute(`/models/${id}`));
  }

  exportModel(modelId: string, ev?: Event): void {
    ev?.stopPropagation();
    if (!modelId) return;

    this.service.getById(modelId).subscribe({
      next: (fullModel) => {
        try {
          this.importExportService.exportToFile(fullModel);
          this.alert.showSuccess(
            this.translate.instant('MODELS.EXPORT.SUCCESS')
          );
        } catch (err) {
          this.alert.showError(err);
        }
      },
      error: (err) => {
        this.alert.showError(err);
      },
    });
  }

  loadModels(): void {
    this.loading = true;
    const tagFilter = this.currentFilters.selectedTag?.trim() || undefined;
    const searchFilter = this.currentFilters.searchTerm?.trim() || undefined;
    const isDeployedFilter = this.deployFilter === 'all' ? undefined : this.deployFilter === 'deployed';

    this.service.list(tagFilter, searchFilter, this.currentPage, this.pageSize, isDeployedFilter).subscribe({
      next: (response) => {
        this.models = response.models || [];
        this.totalItems = response.total;
        this.totalPages = response.total_pages;
        this.filteredModels = [...this.models];
        this.loading = false;
      },
      error: (err) => {
        this.alert.showError(err);
        this.loading = false;
        this.models = [];
        this.filteredModels = [];
        this.totalItems = 0;
        this.totalPages = 0;
      }
    });
  }

  onPageChange(page: number): void {
    this.currentPage = page;
    this.loadModels();
  }

  private applyLocalFilters(): void {
    this.filteredModels = [...this.models];
  }

  deleteModel(id: string, ev?: Event) {
    ev?.stopPropagation();
    if (!id) return;

    const model = this.models.find(m => (m.id ?? m.name) === id);
    if (!model) return;

    this.modelToDelete = {
      id: model.id ?? model.name,
      name: model.name,
      created_date: model.created_at ? this.dateFormatter.formatDate(model.created_at) : '',
      created_time: model.created_at ? this.dateFormatter.formatTime(model.created_at) : '',
      tag: model.tag || null
    };
    this.modelImpactItems = [
      this.translate.instant('MODELS.DELETE_IMPACT.DATA'),
      this.translate.instant('MODELS.DELETE_IMPACT.TAGS'),
      this.translate.instant('MODELS.DELETE_IMPACT.BLOCKED'),
    ];
    this.forceDeleteCheckboxLabel = this.translate.instant('MODELS.DELETE_IMPACT.FORCE_LABEL');
    this.forceDeleteCheckboxWarning = this.translate.instant('MODELS.DELETE_IMPACT.FORCE_WARNING');
    this.showDeleteModal = true;
  }

  onDeleteConfirm(force: boolean = false) {
    if (this.modelToDelete) {
      this.service.delete(String(this.modelToDelete.id), force).subscribe({
        next: () => {
          this.alert.showSuccess(this.translate.instant('MODELS.DELETE.SUCCESS'));
          this.loadModels();
          this.showDeleteModal = false;
          this.modelToDelete = null;
        },
        error: (err) => {
          this.alert.showError(err);
          this.showDeleteModal = false;
          this.modelToDelete = null;
        }
      });
    }
  }

  onDeleteClose() {
    this.showDeleteModal = false;
    this.modelToDelete = null;
    this.modelImpactItems = [];
    this.forceDeleteCheckboxLabel = '';
    this.forceDeleteCheckboxWarning = '';
  }

  handleLinkTag(item: ListItem): void {
    const taggableItem: TaggableItem = {
      id: String(item.id),
      name: item.name,
      tag: item.tag as Tag
    };
    this.tagManager?.openLinkModal(taggableItem);
  }

  handleUnlinkTag(item: ListItem): void {
    const taggableItem: TaggableItem = {
      id: String(item.id),
      name: item.name,
      tag: item.tag as Tag
    };
    this.tagManager?.openUnlinkModal(taggableItem);
  }

  onTagChanged(): void {
    this.loadModels();
  }
}
